import makeStyles from '@mui/styles/makeStyles';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {ArrowLeft, MoreVertical, Video} from "lucide-react";
import {get, map, partition} from "lodash";
import moment from "moment";
import React, {useEffect, useState} from "react";
import {Link, useHistory, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {callDateParts, completedCalls, isCallLive, sessionForCall, upcomingCalls} from "business/upcomingCalls";

import {color, font, radius, space, type} from "common/tokens";
import AddEventForm from "components/AddEventForm";
import BookingConfirmation from "components/redesign/BookingConfirmation";
import Skeleton from "components/redesign/Skeleton";
import CalendarIntegrationOptions from "components/CalendarIntegrationOptions";
import Card from "components/redesign/Card";
import CtaButton from "components/redesign/CtaButton";
import EventList from "components/redesign/EventList";
import EventSeriesDialog, {seriesOptions, singleOption} from "components/EventSeriesDialog";

const useStyles = makeStyles(theme => ({
  page: {
    background: color.SURFACE,
    minHeight: '100%',
  },
  container: {
    width: '100%',
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '24px 32px 64px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    [theme.breakpoints.down('md')]: {
      padding: '16px 16px 48px',
    },
  },
  backLink: {
    ...type.body,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: color.TEXT_MUTED,
    textDecoration: 'none',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    flexWrap: 'wrap',
  },
  title: {
    ...type.pageTitle,
    color: color.TEXT,
    margin: 0,
  },
  syncRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    flexWrap: 'wrap',
  },
  divider: {
    height: 1,
    background: color.DIVIDER,
    width: '100%',
  },
  hint: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  review: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    display: 'inline-flex',
    alignItems: 'center',
    height: 40,
    padding: '0 16px',
    borderRadius: radius.CONTROL,
    border: 'none',
    background: color.TERTIARY,
    color: color.ON_TERTIARY,
    cursor: 'pointer',
  },
  menuButton: {
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 6,
    display: 'flex',
    borderRadius: '50%',
    cursor: 'pointer',
    color: color.TEXT_MUTED,
    '&:hover': {
      background: color.SURFACE_MUTED,
    },
  },
  menuItem: {
    ...type.body,
    color: color.TEXT,
    minWidth: 160,
  },
  menuItemDelete: {
    color: color.DANGER,
  },
  joinLive: {
    ...type.meta,
    fontWeight: font.BOLD,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    height: 40,
    padding: '8px 16px 8px 14px',
    borderRadius: radius.CONTROL,
    border: 'none',
    background: color.TERTIARY,
    color: color.ON_TERTIARY,
    cursor: 'pointer',
  },
  joinDisabled: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    padding: '0 14px',
    borderRadius: 8,
    background: color.SURFACE_MUTED,
    border: `1px solid ${color.DIVIDER}`,
    color: color.TEXT_SUBTLE,
    opacity: 0.7,
    cursor: 'default',
  },
  empty: {
    padding: '64px 32px',
    marginTop: 16,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  emptyTitle: {
    ...type.cardHeading,
    color: color.TEXT,
    margin: 0,
  },
  emptyBody: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  emptyCta: {
    marginTop: 16,
  },
}));

// Edit and delete are secondary to joining the call, so they sit behind the
// row's overflow menu rather than competing with it.
const RowMenu = ({event, onEdit, onDelete}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [anchor, setAnchor] = useState(null);

  const choose = action => () => {
    setAnchor(null);
    action(event);
  };

  return (
    <>
      <button
        type={'button'}
        className={classes.menuButton}
        aria-label={t('moreActions')}
        onClick={e => setAnchor(e.currentTarget)}
      >
        <MoreVertical size={18} strokeWidth={2}/>
      </button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        getContentAnchorEl={null}
      >
        <MenuItem className={classes.menuItem} onClick={choose(onEdit)}>
          {t('edit')}
        </MenuItem>
        <MenuItem
          className={`${classes.menuItem} ${classes.menuItemDelete}`}
          onClick={choose(onDelete)}
        >
          {t('deleteEvent')}
        </MenuItem>
      </Menu>
    </>
  );
};

const AllEvents = ({
  mentorshipId,
  mentorshipGroupId,
  profileId,
  events,
  eventsLoaded,
  sessions,
  calendarIntegrations,
  initMentorship,
  initMentorshipSessions,
  loadEvents,
  loadCalendarIntegrations,
  saveCalendarIntegration,
  unlinkCalendarIntegration,
  submitEvent,
  cancelEvent,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [eventForm, setEventForm] = useState({});
  const [booked, setBooked] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [appliedTo, setAppliedTo] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Arriving from the home's empty state, which asks for the create dialog
  // rather than just the page.
  useEffect(() => {
    if (get(location, 'state.openEventForm')) {
      setEventForm({state: true, eventType: 'addEvent'});
      history.replace(location.pathname);
    }
  }, [location, history]);

  useEffect(() => {
    loadCalendarIntegrations();
  }, [loadCalendarIntegrations]);

  useEffect(() => {
    initMentorshipSessions();
  }, [initMentorshipSessions]);

  // Opened directly, nothing else has fetched the mentorship, so the session
  // picker and the creator id would both be empty.
  useEffect(() => {
    if (mentorshipId) {
      initMentorship(mentorshipId);
      loadEvents(mentorshipId);
    }
  }, [mentorshipId, initMentorship, loadEvents]);

  const upcoming = upcomingCalls(events);
  const past = completedCalls(events);
  const [today, later] = partition(upcoming, event =>
    moment(get(event, 'scheduledTime')).isSame(moment(), 'day'));

  const openCall = event => {
    const uuid = get(event, 'videos[0].sessionUuid');
    if (uuid) {
      history.push(`/video/join-meeting/${uuid}?section=video`);
    }
  };

  const openEditor = event =>
    setEventForm({state: true, eventType: 'editEvent', EventId: get(event, 'id')});

  const askDelete = event => {
    setAppliedTo(null);
    setPendingDelete(event);
  };

  const confirmDelete = async applyTo => {
    setAppliedTo(applyTo);
    setDeleting(true);
    try {
      await cancelEvent(pendingDelete, applyTo, mentorshipId);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // The event stores the session's title, not its id; sessionForCall does the
  // name lookup over session statuses, so the flat list is shaped to match.
  const sessionIdFor = event =>
    get(sessionForCall(event, map(sessions, session => ({session}))), 'session.id');

  const reviewButton = event => {
    const sessionId = sessionIdFor(event);
    return Boolean(sessionId) && (
      <button
        type={'button'}
        className={classes.review}
        onClick={() => history.push(`/mentorship/${sessionId}/summary`)}
      >
        {t('reviewSessionMaterial')}
      </button>
    );
  };

  // Review the material, then join — the link only opens ten minutes before
  // the call, so Join is a disabled pill until then.
  const upcomingActions = event => (
    <>
      {reviewButton(event)}
      {isCallLive(event) ? (
        <button type={'button'} className={classes.joinLive} onClick={() => openCall(event)}>
          <Video size={18} strokeWidth={2}/>
          {t('joinCall')}
        </button>
      ) : (
        <span className={classes.joinDisabled}>{t('joinCall')}</span>
      )}
      <RowMenu event={event} onEdit={openEditor} onDelete={askDelete}/>
    </>
  );

  const pastActions = event =>
    <RowMenu event={event} onEdit={openEditor} onDelete={askDelete}/>;

  const hasEvents = Boolean(today.length || later.length || past.length);

  return (
    <div className={classes.page}>
      <div className={`${classes.container} ui-enter`}>
        <Link to={'/mentorship'} className={classes.backLink}>
          <ArrowLeft size={16} strokeWidth={2}/>
          {t('mentorship')}
        </Link>

        <div className={classes.titleRow}>
          <h1 className={classes.title}>{t('allEventsTitle')}</h1>
          <CtaButton
            size={'large'}
            variant={'primary'}
            label={t('bookAnotherEvent')}
            onClick={() => setEventForm({state: true, eventType: 'addEvent'})}
          />
        </div>

        <div className={classes.syncRow}>
          <CalendarIntegrationOptions
            saveCalendarIntegration={saveCalendarIntegration}
            calendarIntegrations={calendarIntegrations}
            unlinkCalendarIntegration={unlinkCalendarIntegration}
            loadCalendarIntegrations={loadCalendarIntegrations}
          />
          <p className={classes.hint}>{t('callLinkAvailableHint')}</p>
        </div>

        <span className={classes.divider}/>

        {!eventsLoaded && (
          <div className={classes.sections}>
            <Skeleton width={90} height={17}/>
            <Skeleton height={64}/>
            <Skeleton height={64}/>
            <Skeleton height={64}/>
          </div>
        )}

        {eventsLoaded && hasEvents ? (
          <div className={classes.sections}>
            <EventList
              label={t('todayCaps')}
              events={today}
              variant={'card'}
              isLive={isCallLive}
              renderActions={upcomingActions}
              meta={event => `${t('todayCaps')} · ${callDateParts(event).time}`}
            />
            <EventList
              label={t('upcomingCaps')}
              events={later}
              showDivider
              renderActions={upcomingActions}
            />
            {/* Folded away by default: the page is for what is coming up, and
                the header says how much history is behind it. */}
            <EventList
              label={t('completedEvents', {count: past.length})}
              events={past}
              variant={'past'}
              showDivider
              collapsible
              renderActions={pastActions}
              meta={() => t('callEndedHint')}
            />
          </div>
        ) : eventsLoaded && (
          <Card bordered className={classes.empty}>
            <p className={classes.emptyTitle}>{t('noCallsScheduled')}</p>
            <p className={classes.emptyBody}>{t('noCallsScheduledBody')}</p>
            <CtaButton
              className={classes.emptyCta}
              size={'large'}
              variant={'primary'}
              label={t('bookAnotherEvent')}
              onClick={() => setEventForm({state: true, eventType: 'addEvent'})}
            />
          </Card>
        )}

        <AddEventForm
          open={eventForm}
          handleClose={() => setEventForm({})}
          events={events}
          sessions={sessions}
          profileId={profileId}
          submitEvent={submitEvent}
          cancelEvent={cancelEvent}
          mentorshipId={mentorshipId}
          mentorshipGroupId={mentorshipGroupId}
          onSubmitted={(isEdit) => setBooked({isEdit})}
        />

        <EventSeriesDialog
          open={Boolean(pendingDelete)}
          title={t('deleteEvent')}
          prompt={get(pendingDelete, 'isRecurringEvent')
            ? t('deleteRecurringEventPrompt')
            : t('deleteEventConfirmPrompt')}
          options={get(pendingDelete, 'isRecurringEvent')
            ? seriesOptions(t)
            : singleOption(t('deleteCaps'))}
          chosen={appliedTo}
          chosenVariant={'danger'}
          busy={deleting}
          onChoose={confirmDelete}
          onDismiss={() => setPendingDelete(null)}
        />

        {/* Confirms what was booked once the form has closed, rather than
            dropping the reader back on the list with nothing to check. */}
        {Boolean(booked) && (
          <BookingConfirmation isEdit={booked.isEdit} onClose={() => setBooked(null)}/>
        )}
      </div>
    </div>
  );
};

export default AllEvents;
